import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowMonitoringApiService } from '../services/workflow-monitoring-api.service';
import {
  workflowExitReasonLabel,
  workflowNodeSubtypeLabel,
  workflowNodeTypeLabel,
  workflowRunTypeLabel,
  workflowObjectTypeLabel,
  workflowStatusLabel
} from '../models/workflow-labels';
import {
  WorkflowEnrollmentTimelineResponse,
  WorkflowRunTimeline,
  WorkflowStepTimeline
} from '../models/workflow-monitoring.model';
import { WorkflowNodeLabelService } from '../shared/workflow-node-label.service';

@Component({
  selector: 'app-workflow-timeline-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './workflow-timeline-modal.component.html'
})
export class WorkflowTimelineModalComponent implements OnInit, OnDestroy, OnChanges {
  /**
   * Etiquetas legibles: diccionario único del módulo (`models/workflow-labels.ts`). Se usan como
   * RESPALDO de los campos `*Label` que resuelve el backend — cuando el DTO no los trae, la
   * plantilla pintaba el enum del motor en crudo.
   */
  readonly statusText = workflowStatusLabel;
  readonly runTypeText = workflowRunTypeLabel;
  readonly nodeTypeText = workflowNodeTypeLabel;
  readonly nodeSubtypeText = workflowNodeSubtypeLabel;
  readonly exitReasonText = workflowExitReasonLabel;

  @Input() enrollmentId: number | null = null;
  @Input() includeTestRuns = false;
  @Output() closed = new EventEmitter<void>();

  timeline: WorkflowEnrollmentTimelineResponse | null = null;
  loading = false;
  error = '';

  expandedRuns = new Set<number>();
  expandedStepOutputs = new Set<number>();

  // Retry modal
  showRetryModal = false;
  retryStepId: number | null = null;
  retryReason = '';
  retrying = false;
  retryError = '';
  retrySuccess = '';

  private destroy$ = new Subject<void>();

  constructor(
    private monitoringApi: WorkflowMonitoringApiService,
    private nodeLabels: WorkflowNodeLabelService
  ) {}

  /** Nombre del nodo del paso; «Nodo #id» si el grafo aún no llegó o el nodo ya no existe. */
  nodeText(nodeId: number | null | undefined): string {
    const workflowId = this.timeline?.enrollment?.workflowId;
    return workflowId ? this.nodeLabels.labelFor(workflowId, nodeId) : (nodeId ? `Nodo #${nodeId}` : '—');
  }

  ngOnInit(): void { this.load(); }
  ngOnChanges(): void { if (this.enrollmentId) this.load(); }
  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  load(): void {
    if (!this.enrollmentId) return;
    this.loading = true;
    this.error = '';
    this.monitoringApi.getEnrollmentTimeline(this.enrollmentId, {
      includeTestRuns: this.includeTestRuns
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.timeline = res;
        this.loading = false;
        // Nombres de nodo: los pasos solo traen `nodeId`, un entero de `workflow_node`.
        if (res.enrollment?.workflowId) {
          this.nodeLabels.load(res.enrollment.workflowId).pipe(takeUntil(this.destroy$)).subscribe();
        }
        // Auto-expand first run
        if (res.runs.length > 0) this.expandedRuns.add(res.runs[0].id);
      },
      error: (err) => {
        this.error = err.error?.message ?? 'No se pudo cargar el timeline.';
        this.loading = false;
      }
    });
  }

  toggleRun(runId: number): void {
    if (this.expandedRuns.has(runId)) this.expandedRuns.delete(runId);
    else this.expandedRuns.add(runId);
  }

  toggleStepOutput(stepId: number): void {
    if (this.expandedStepOutputs.has(stepId)) this.expandedStepOutputs.delete(stepId);
    else this.expandedStepOutputs.add(stepId);
  }

  isRetryable(step: WorkflowStepTimeline): boolean {
    const retryableTypes = ['ACTION', 'WAIT'];
    const retryableStatuses = ['FAILED', 'RETRY_SCHEDULED'];
    return retryableTypes.includes(step.stepType?.toUpperCase() ?? '') &&
           retryableStatuses.includes(step.status?.toUpperCase() ?? '');
  }

  openRetryModal(stepId: number): void {
    this.retryStepId = stepId;
    this.retryReason = '';
    this.retryError = '';
    this.retrySuccess = '';
    this.showRetryModal = true;
  }

  confirmRetry(): void {
    if (!this.retryStepId) return;
    this.retrying = true;
    this.retryError = '';
    this.monitoringApi.retryExecutionStep(this.retryStepId, this.retryReason.trim() || undefined)
      .pipe(takeUntil(this.destroy$)).subscribe({
        next: (res) => {
          this.retrySuccess = `Paso reintentado correctamente. Estado: ${res.status}`;
          this.retrying = false;
          this.showRetryModal = false;
          this.load();
        },
        error: (err) => {
          const body = err?.error ?? {};
          const code = body?.code ?? body?.errorCode ?? '';
          switch (code) {
            case 'WORKFLOW_STEP_NOT_RETRYABLE':
              this.retryError = 'Este paso no puede reintentarse.'; break;
            case 'WORKFLOW_STEP_RETRY_FAILED':
              this.retryError = 'No se pudo reintentar el paso.'; break;
            case 'WORKFLOW_STEP_RETRY_UNSUPPORTED_TYPE':
              this.retryError = 'Este tipo de paso no soporta reintento manual.'; break;
            case 'WORKFLOW_STEP_NOT_FOUND':
              this.retryError = 'No se encontró el paso de ejecución.'; break;
            default:
              this.retryError = body?.message ?? 'Error al reintentar.';
          }
          this.retrying = false;
        }
      });
  }

  closeRetryModal(): void {
    this.showRetryModal = false;
    this.retryStepId = null;
    this.retryReason = '';
    this.retryError = '';
  }

  // ── Formatters ────────────────────────────────────────────────────────────

  /**
   * inc40 — identifica el registro inscrito por su nombre en lugar de «CONTACT #255».
   * El tipo traducido y el nombre los resuelve el backend; si el registro ya no existe se
   * cae al tipo + id, que sigue siendo información válida de un histórico.
   */
  get registroLabel(): string {
    const enrollment = this.timeline?.enrollment;
    if (!enrollment) return '';
    const tipo = enrollment.objectTypeLabel || workflowObjectTypeLabel(enrollment.objectType, enrollment.objectType);
    return enrollment.objectName
      ? `${tipo}: ${enrollment.objectName} · #${enrollment.objectId}`
      : `${tipo} #${enrollment.objectId}`;
  }

  formatDuration(ms: number | null | undefined): string {
    if (!ms) return '—';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
  }

  formatTimeUntil(ms: number | null | undefined): string {
    if (!ms || ms <= 0) return 'Inminente';
    if (ms < 60000) return `${Math.floor(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
    return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
  }

  getRunBadgeClass(runType: string): string {
    return runType === 'TEST'
      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
      : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
  }

  getStepStatusClass(status: string): string {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':          return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'FAILED':           return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'SKIPPED':          return 'bg-gray-100 text-gray-500 dark:bg-gray-700/40 dark:text-gray-400';
      case 'RETRY_SCHEDULED':  return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'WAITING':          return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:                 return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    }
  }

  hasJson(val: any): boolean {
    if (!val) return false;
    if (typeof val === 'string') return val !== '{}' && val !== 'null';
    if (typeof val === 'object') return Object.keys(val).length > 0;
    return false;
  }
}
